import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { rolesApi } from './roles.api';
import { roleKeys } from './roles.queries';
import type {
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignRoleRequest,
  RolesListParams
} from '../types';

export function useCreateRoleMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: (data: CreateRoleRequest) => rolesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useUpdateRoleMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleRequest }) =>
      rolesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useDeleteRoleMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      rolesApi.delete({ id, force }),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useDuplicateRoleMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: (id: number) => rolesApi.duplicate(id),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('duplicateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useAssignRoleMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: ({
      userId,
      data
    }: {
      userId: number;
      data: AssignRoleRequest;
    }) => rolesApi.assignToUser(userId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('assignSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useBulkDeleteRolesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[]; force?: boolean }
        | { all: true; filters: Partial<RolesListParams>; force?: boolean }
    ) => rolesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}

export function useBulkAssignPermissionsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('roles');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[]; permissions: string[] }
        | {
            all: true;
            filters: Partial<RolesListParams>;
            permissions: string[];
          }
    ) => rolesApi.bulkAssignPermissions(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkAssignSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.metrics() });
    }
  });
}
